import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Admin dialog to set Base44 URL / app id / api key for a given mode.
 * Payload tries the most likely backend shape; surface server errors verbatim
 * so a misnamed field is fixable by reading the error.
 */
export default function Base44ConfigDialog({ open, mode, cfg, onClose, onSaved }) {
  const [url, setUrl] = useState("");
  const [appId, setAppId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setUrl(cfg?.url || "");
      setAppId(cfg?.app_id || "");
      setApiKey("");
      setError("");
    }
  }, [open, cfg]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const payload = { mode };
    if (url.trim()) payload.url = url.trim();
    if (appId.trim()) payload.app_id = appId.trim();
    if (apiKey.trim()) payload.api_key = apiKey.trim();

    if (Object.keys(payload).length === 1) {
      setError("Enter at least one value to update.");
      return;
    }

    setSaving(true);
    try {
      // Primary: PUT /api/settings/base44 with { mode, url, app_id, api_key }
      await api.put("/settings/base44", payload);
      onSaved?.();
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      // If schema differs (e.g. nested), retry once with { [mode]: {...} } envelope
      if (err?.response?.status === 422 || /unknown\s+field|extra\s+inputs/i.test(detail || "")) {
        try {
          const inner = { ...payload };
          delete inner.mode;
          await api.put("/settings/base44", { [mode]: inner });
          onSaved?.();
          return;
        } catch (err2) {
          setError(formatApiErrorDetail(err2?.response?.data?.detail) || "Update failed");
          return;
        }
      }
      setError(detail || "Update failed — check that you are signed in as an admin.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="base44-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {mode === "test" ? "Test Base44 credentials" : "Live Base44 credentials"}
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            These live on the backend. Other admins and the mobile app will use the same values.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Base44 URL</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://app.base44.com/api/apps/<app-id>"
              data-testid="base44-url"
              autoComplete="off"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Full base URL with no trailing slash.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">App ID</Label>
            <Input
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="e.g. 6789abcd-..."
              data-testid="base44-app-id"
              autoComplete="off"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">API key</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg?.has_api_key || cfg?.api_key_masked ? "Leave blank to keep existing" : "Paste your Base44 API key"}
              data-testid="base44-api-key"
              autoComplete="off"
            />
            <p className="text-[10px] text-zinc-500 mt-1">
              Stored encrypted on the backend. We never display it in plain text after saving.
            </p>
          </div>

          {error && (
            <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2" data-testid="base44-error">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="base44-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#C8102E] hover:bg-[#A00D25]"
              data-testid="base44-save"
            >
              {saving ? "Saving…" : "Save credentials"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
